import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
    BarChart3,
    Map as MapIcon,
    Users,
    Settings,
    LogOut,
    Shield,
    Bell,
    Menu,
    X,
    Brain,
    Truck,
    Sliders,
    UserCog
} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';
import { useNavigationHistory } from '../hooks/useNavigationHistory';
import { useWells } from '../hooks/useWells';
import { useNotifications } from '../hooks/useNotifications';
import ExitConfirmDialog from '../components/ExitConfirmDialog';

// Feature Components
import OperationsDashboard from '../components/manager/OperationsDashboard';
import ManagerInsights from '../components/manager/ManagerInsights';
import WellAssignmentManager from '../components/manager/WellAssignmentManager';
import LogisticsCommand from '../components/manager/LogisticsCommand';
import ScenarioPlanner from '../components/manager/ScenarioPlanner';
import SatelliteCommand from '../components/map/SatelliteCommand';
import TankMonitor from '../components/map/TankMonitor';
import SurveillanceHub from '../components/map/SurveillanceHub';
import IndustryMap from '../components/manager/IndustryMap';
import DataEntry from '../components/DataEntry';
import ServiceTickets from '../components/ServiceTickets';
import TaskManager from '../components/TaskManager';
import UserManagement from '../components/UserManagement';
// Standard settings tab but we'll use it in a restricted mode or full mode for manager
import SettingsTab from '../components/SettingsTab';
// Assuming ManagerTimeClock exists in components based on project structure
import ManagerTimeClock from '../components/ManagerTimeClock';



interface ManagerAppProps {
    user: any;
    onLogout: () => void;
    onSwitchToFieldView: () => void;
    onUserUpdate?: (user: any) => void;
}

export default function ManagerPortal({ user, onLogout, onSwitchToFieldView, onUserUpdate }: ManagerAppProps) {
    const { isDarkMode } = useTheme(user.uid);
    
    // Determine initial tab from URL
    const [activeTab, setActiveTab] = useState<'dashboard' | 'insights' | 'logistics' | 'scenarios' | 'fleet' | 'personnel' | 'assignments' | 'approvals' | 'settings'>(() => {
        const path = window.location.pathname;
        if (path.includes('/well-assignments')) return 'assignments';
        if (path.includes('/insights')) return 'insights';
        if (path.includes('/logistics')) return 'logistics';
        if (path.includes('/scenarios')) return 'scenarios';
        if (path.includes('/fleet')) return 'fleet';
        if (path.includes('/personnel')) return 'personnel';
        if (path.includes('/approvals')) return 'approvals';
        if (path.includes('/settings')) return 'settings';
        return 'dashboard';
    });

    const [showMobileSidebar, setShowMobileSidebar] = useState(false);

    // Use the same hooks as PumpersPortal for proper tenant handling
    const { wells, refreshWells } = useWells(user?.uid, user);
    const { notifications: _notifications } = useNotifications();

    // Feature Modals
    const [showTankMonitor, setShowTankMonitor] = useState(false);
    const [showSurveillance, setShowSurveillance] = useState(false);
    const [showIndustryMap, setShowIndustryMap] = useState(false);
    const [showDataEntry, setShowDataEntry] = useState(false);
    const [showServiceTickets, setShowServiceTickets] = useState(false);
    const [showTaskManager, setShowTaskManager] = useState(false);
    const [monitorWell, setMonitorWell] = useState<any>(null);
    const [selectedWell, setSelectedWell] = useState<any>(null);
    const [taskWellId, setTaskWellId] = useState<string | null>(null);

    // Map tabs to paths
    const getPathForTab = (tab: string) => {
        switch (tab) {
            case 'assignments': return '/manager/well-assignments';
            case 'dashboard': return '/manager/dashboard';
            default: return `/manager/${tab}`;
        }
    };

    // 📱 Navigation History - Enable proper back button behavior for PWA
    const { showExitConfirmDialog, handleConfirmExit, handleCancelExit } = useNavigationHistory({
        activeTab,
        setActiveTab: setActiveTab as (tab: string) => void,
        selectedWellId: selectedWell?.id || null,
        setSelectedWellId: (id) => {
            if (id) {
                const well = wells.find(w => w.id === id);
                setSelectedWell(well || null);
            } else {
                setSelectedWell(null);
            }
        },
        path: getPathForTab(activeTab),
        options: {
            defaultTab: 'dashboard',
            preventExit: true,
            showExitDialog: true
        }
    });

    // Layout Constants
    // const SIDEBAR_WIDTH = 280; // Deprecated for top nav

    return (
        <div className={`min-h-screen ${isDarkMode ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col transition-colors duration-200`}>

            {/* Top Navigation Bar */}
            <header className="bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-slate-800 sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        {/* Logo & Brand */}
                        <div className="flex items-center gap-2">
                            <Shield className="w-6 h-6 text-blue-600" />
                            <span className="font-bold text-lg tracking-tight hidden md:block">Command Center</span>
                        </div>

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center space-x-1">
                            <NavButton
                                active={activeTab === 'dashboard'}
                                onClick={() => setActiveTab('dashboard')}
                                icon={BarChart3}
                                label="Overview"
                            />
                            <NavButton
                                active={activeTab === 'insights'}
                                onClick={() => setActiveTab('insights')}
                                icon={Brain}
                                label="Insights"
                                badge="AI"
                            />
                            <NavButton
                                active={activeTab === 'logistics'}
                                onClick={() => setActiveTab('logistics')}
                                icon={Truck}
                                label="Logistics"
                            />
                            <NavButton
                                active={activeTab === 'scenarios'}
                                onClick={() => setActiveTab('scenarios')}
                                icon={Sliders}
                                label="Planning"
                            />
                            <NavButton
                                active={activeTab === 'fleet'}
                                onClick={() => setActiveTab('fleet')}
                                icon={MapIcon}
                                label="Fleet"
                                badge="Live"
                            />
                            <NavButton
                                active={activeTab === 'personnel'}
                                onClick={() => setActiveTab('personnel')}
                                icon={Users}
                                label="Personnel"
                            />
                            <NavButton
                                active={activeTab === 'assignments'}
                                onClick={() => setActiveTab('assignments')}
                                icon={UserCog}
                                label="Assignments"
                            />
                            <NavButton
                                active={activeTab === 'approvals'}
                                onClick={() => setActiveTab('approvals')}
                                icon={Shield}
                                label="Approvals"
                                badge="3"
                            />
                            <NavButton
                                active={activeTab === 'settings'}
                                onClick={() => setActiveTab('settings')}
                                icon={Settings}
                                label="Settings"
                            />
                        </nav>

                        {/* User & Actions */}
                        <div className="flex items-center gap-3">
                            <Button variant="ghost" size="icon" className="text-gray-500">
                                <Bell className="w-5 h-5" />
                            </Button>

                            <div className="h-8 w-px bg-gray-200 dark:bg-slate-700 mx-1 hidden md:block"></div>

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-3 outline-none">
                                        <div className="hidden md:block text-right">
                                            <div className="text-sm font-medium">{user.name}</div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400">Manager</div>
                                        </div>
                                        <div className="w-8 h-8 rounded-full bg-indigo-500 flex items-center justify-center text-white font-bold hover:ring-2 hover:ring-indigo-200 transition-all">
                                            {user.name?.[0] || 'M'}
                                        </div>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel>My Account</DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={onSwitchToFieldView}>
                                        <MapIcon className="mr-2 h-4 w-4" />
                                        <span>Switch to Field View</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => setActiveTab('settings')}>
                                        <Settings className="mr-2 h-4 w-4" />
                                        <span>Settings</span>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={onLogout} className="text-red-600 focus:text-red-600">
                                        <LogOut className="mr-2 h-4 w-4" />
                                        <span>Sign out</span>
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Mobile Menu Button */}
                            <button
                                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800"
                                onClick={() => setShowMobileSidebar(!showMobileSidebar)}
                            >
                                {showMobileSidebar ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Mobile Navigation Menu */}
                {showMobileSidebar && (
                    <div className="md:hidden border-t border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                        <div className="px-2 pt-2 pb-3 space-y-1">
                            <NavButton
                                active={activeTab === 'dashboard'}
                                onClick={() => { setActiveTab('dashboard'); setShowMobileSidebar(false); }}
                                icon={BarChart3}
                                label="Overview"
                            />
                            <NavButton
                                active={activeTab === 'insights'}
                                onClick={() => { setActiveTab('insights'); setShowMobileSidebar(false); }}
                                icon={Brain}
                                label="Insights"
                                badge="AI"
                            />
                            <NavButton
                                active={activeTab === 'logistics'}
                                onClick={() => { setActiveTab('logistics'); setShowMobileSidebar(false); }}
                                icon={Truck}
                                label="Logistics"
                            />
                            <NavButton
                                active={activeTab === 'scenarios'}
                                onClick={() => { setActiveTab('scenarios'); setShowMobileSidebar(false); }}
                                icon={Sliders}
                                label="Planning"
                            />
                            <NavButton
                                active={activeTab === 'fleet'}
                                onClick={() => { setActiveTab('fleet'); setShowMobileSidebar(false); }}
                                icon={MapIcon}
                                label="Fleet Map"
                                badge="Live"
                            />
                            <NavButton
                                active={activeTab === 'personnel'}
                                onClick={() => { setActiveTab('personnel'); setShowMobileSidebar(false); }}
                                icon={Users}
                                label="Personnel"
                            />
                            <NavButton
                                active={activeTab === 'assignments'}
                                onClick={() => { setActiveTab('assignments'); setShowMobileSidebar(false); }}
                                icon={UserCog}
                                label="Assignments"
                            />
                            <NavButton
                                active={activeTab === 'approvals'}
                                onClick={() => { setActiveTab('approvals'); setShowMobileSidebar(false); }}
                                icon={Shield}
                                label="Approvals"
                                badge="3"
                            />
                            <NavButton
                                active={activeTab === 'settings'}
                                onClick={() => { setActiveTab('settings'); setShowMobileSidebar(false); }}
                                icon={Settings}
                                label="Settings"
                            />
                            <div className="border-t border-gray-200 dark:border-slate-800 my-2 pt-2">
                                <Button
                                    variant="outline"
                                    className="w-full justify-start gap-2 mb-2"
                                    onClick={onSwitchToFieldView}
                                >
                                    <MapIcon className="w-4 h-4" />
                                    Switch to Field View
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="w-full justify-start gap-2 text-red-600"
                                    onClick={onLogout}
                                >
                                    <LogOut className="w-4 h-4" />
                                    Sign Out
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </header>

            {/* Main Content Area */}
            <main className="flex-1 overflow-auto bg-gray-50 dark:bg-slate-950">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    {activeTab === 'dashboard' && (
                        <OperationsDashboard
                            onNavigateToFleet={() => setActiveTab('fleet')}
                            onNavigateToApprovals={() => setActiveTab('approvals')}
                            onNavigateToAssignments={() => setActiveTab('assignments')}
                        />
                    )}
                    {activeTab === 'insights' && <ManagerInsights companyId={user?.companyId || user?.companyId || user?.companyId} />}
                    {activeTab === 'logistics' && <LogisticsCommand companyId={user?.companyId || user?.companyId || user?.companyId} />}
                    {activeTab === 'scenarios' && <ScenarioPlanner companyId={user?.companyId || user?.companyId || user?.companyId} />}
                    {activeTab === 'fleet' && (
                        <SatelliteCommand
                            wells={wells}
                            onAddEntry={(well) => {
                                // Manager can add/edit entries directly
                                setSelectedWell(well);
                                setShowDataEntry(true);
                            }}
                            onCreateTicket={(well) => {
                                setSelectedWell(well);
                                setShowServiceTickets(true);
                            }}
                            onCreateTask={(well) => {
                                setTaskWellId(well.id);
                                setShowTaskManager(true);
                            }}
                            onNavigate={(well) => {
                                // Open Google Maps navigation to well
                                const lat = well.latitude || well.coordinates?.lat;
                                const lng = well.longitude || well.coordinates?.lng;
                                if (lat && lng) {
                                    window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`, '_blank');
                                }
                            }}
                        />
                    )}
                    {activeTab === 'personnel' && <UserManagement />}
                    {activeTab === 'assignments' && (
                        <WellAssignmentManager
                            companyId={user?.companyId}
                            isDarkMode={isDarkMode}
                        />
                    )}
                    {activeTab === 'approvals' && (
                        <ManagerTimeClock
                            managerId={user.uid}
                            managerName={user.name || 'Manager'}
                            companyId={user.companyId}
                            onClose={() => setActiveTab('dashboard')}
                        />
                    )}
                    {activeTab === 'settings' && (
                        <SettingsTab
                            user={user}
                            wells={wells}
                            onUserUpdate={onUserUpdate}
                            onToggleTheme={() => window.location.reload()}
                            onShowTaskManager={() => {
                                setShowTaskManager(true);
                            }}
                            onNavigate={(section) => {
                                // Map settings sections to top-level tabs if applicable
                                const sectionLower = section.toLowerCase();

                                // Handle navigation to different sections
                                if (sectionLower === 'users' || sectionLower === 'personnel') {
                                    setActiveTab('personnel');
                                } else if (sectionLower === 'fleet' || sectionLower === 'map' || sectionLower === 'satellite') {
                                    setActiveTab('fleet');
                                } else if (sectionLower === 'industrymap') {
                                    setShowIndustryMap(true);
                                } else if (sectionLower === 'alerts' || sectionLower === 'dashboard') {
                                    setActiveTab('dashboard');
                                } else if (sectionLower === 'insights' || sectionLower === 'ai') {
                                    setActiveTab('insights');
                                } else if (sectionLower === 'logistics') {
                                    setActiveTab('logistics');
                                } else if (sectionLower === 'scenarios' || sectionLower === 'planning') {
                                    setActiveTab('scenarios');
                                } else if (sectionLower === 'approvals') {
                                    setActiveTab('approvals');
                                } else if (sectionLower === 'tickets') {
                                    setShowServiceTickets(true);
                                } else if (sectionLower === 'billing' || sectionLower === 'compare' || sectionLower === 'gallery' || sectionLower === 'equipment') {
                                    // These are available in Field View - give manager direct access
                                    onSwitchToFieldView();
                                    toast.info(`Opening ${section} in Field View`);
                                } else {
                                    // For other sections, show toast with info
                                    toast.info(`${section} - Opening in Field View`);
                                    onSwitchToFieldView();
                                }
                            }}
                        />
                    )}

                    {/* Feature Modals - Manager has FULL access to all features */}
                    {showTankMonitor && monitorWell && (
                        <TankMonitor
                            well={monitorWell}
                            onClose={() => setShowTankMonitor(false)}
                            isDarkMode={isDarkMode}
                        />
                    )}
                    {showSurveillance && monitorWell && (
                        <SurveillanceHub
                            well={monitorWell}
                            onClose={() => setShowSurveillance(false)}
                            isDarkMode={isDarkMode}
                        />
                    )}
                    {showIndustryMap && (
                        <IndustryMap
                            wells={wells}
                            onClose={() => setShowIndustryMap(false)}
                            onSelectWell={(well) => {
                                setSelectedWell(well);
                                setShowIndustryMap(false);
                            }}
                            onAddEntry={(well) => {
                                setSelectedWell(well);
                                setShowDataEntry(true);
                                setShowIndustryMap(false);
                            }}
                            onCreateTicket={(well) => {
                                setSelectedWell(well);
                                setShowServiceTickets(true);
                                setShowIndustryMap(false);
                            }}
                            onOpenTanks={(well) => {
                                setMonitorWell(well);
                                setShowTankMonitor(true);
                                setShowIndustryMap(false);
                            }}
                            onOpenCams={(well) => {
                                setMonitorWell(well);
                                setShowSurveillance(true);
                                setShowIndustryMap(false);
                            }}
                        />
                    )}
                    {showDataEntry && selectedWell && (
                        <DataEntry
                            well={selectedWell}
                            wells={wells}
                            onClose={() => {
                                setShowDataEntry(false);
                                refreshWells();
                            }}
                            onSave={() => {
                                setShowDataEntry(false);
                                refreshWells();
                                toast.success('Entry saved successfully');
                            }}
                        />
                    )}
                    {showServiceTickets && (
                        <div className="fixed inset-0 z-50 bg-white dark:bg-slate-900">
                            <ServiceTickets
                                wells={wells}
                                currentUser={user}
                                initialWellId={selectedWell?.id}
                                onClose={() => {
                                    setShowServiceTickets(false);
                                    setSelectedWell(null);
                                }}
                            />
                        </div>
                    )}
                    {showTaskManager && (
                        <TaskManager
                            wells={wells}
                            currentUserId={user.uid}
                            currentUserName={user.name || 'Manager'}
                            initialWellId={taskWellId || undefined}
                            onClose={() => {
                                setShowTaskManager(false);
                                setTaskWellId(null);
                            }}
                        />
                    )}
                </div>
            </main>

            {/* Exit Confirmation Dialog - shown when user presses back twice */}
            <ExitConfirmDialog
                open={showExitConfirmDialog}
                onConfirm={handleConfirmExit}
                onCancel={handleCancelExit}
            />
        </div>
    );
}


// Subcomponents
function NavButton({ active, onClick, icon: Icon, label, badge }: any) {
    return (
        <button
            onClick={onClick}
            className={`
        flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors
        ${active
                    ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800'
                }
      `}
        >
            <Icon className={`w-4 h-4 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
            <span>{label}</span>
            {badge && (
                <span className="ml-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 rounded-md">
                    {badge}
                </span>
            )}
        </button>
    );
}
